import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, Play, Pause } from "lucide-react";
import { WHATSAPP_LINK, TEL_LINK, AUTHOR } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

// Coloque o arquivo da ligação em: frontend/public/ligacao-demo.mp3
const CALL_AUDIO_SRC = "/ligacao-demo.mp3";

const ChatDemo = () => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    const toggleAudio = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) {
            a.play();
            setPlaying(true);
        } else {
            a.pause();
            setPlaying(false);
        }
    };

    return (
    <div
        data-testid="agent-connection-placeholder"
        className="relative w-full rounded-2xl border border-[#1E1E28] bg-[#121218]/80 backdrop-blur-sm p-5 shadow-[0_0_60px_rgba(59,111,255,0.12)]"
    >
        {/* terminal header */}
        <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#7A7A90]/40" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#7A7A90]/40" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#7A7A90]/40" />
            </div>
            <div className="flex items-center gap-2 text-xs text-[#94B4FF]">
                <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3B6FFF] opacity-70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#3B6FFF]" />
                </span>
                Agente ativo
            </div>
        </div>

        {/* messages */}
        <div className="space-y-3 text-sm">
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="max-w-[80%] rounded-2xl rounded-bl-sm bg-[#1E1E28] px-4 py-2.5 text-[#F5F5F5]/90"
            >
                Oi, dá pra marcar uma consulta amanhã de manhã?
            </motion.div>
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 }}
                className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-[#3B6FFF] px-4 py-2.5 text-white"
            >
                Claro! Tenho 9h ou 10h30 amanhã. Qual fica melhor pra você?
            </motion.div>
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.7 }}
                className="max-w-[80%] rounded-2xl rounded-bl-sm bg-[#1E1E28] px-4 py-2.5 text-[#F5F5F5]/90"
            >
                Pode ser 9h 👍
            </motion.div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.3 }}
                className="flex items-center gap-1.5 pl-1 pt-1"
            >
                {[0, 1, 2].map((i) => (
                    <span
                        key={i}
                        className="h-2 w-2 rounded-full bg-[#94B4FF]"
                        style={{
                            animation: "dg-blink 1.4s infinite",
                            animationDelay: `${i * 0.2}s`,
                        }}
                    />
                ))}
                <span className="ml-2 text-xs text-[#7A7A90]">
                    confirmando na agenda…
                </span>
            </motion.div>
        </div>

        {/* real call audio player */}
        <div className="mt-6 flex items-center gap-3 border-t border-[#1E1E28] pt-4">
            <button
                type="button"
                onClick={toggleAudio}
                data-testid="hero-call-audio-toggle"
                aria-label={playing ? "Pausar ligação" : "Ouvir ligação real"}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#3B6FFF] text-white shadow-[0_0_20px_rgba(59,111,255,0.4)] hover:bg-[#2d5be0] transition-colors"
            >
                {playing ? (
                    <Pause className="w-4 h-4" />
                ) : (
                    <Play className="w-4 h-4 translate-x-[1px]" />
                )}
            </button>
            <div className="flex items-end gap-[3px] h-6">
                {[0.3, 0.7, 1, 0.5, 0.9, 0.4, 0.8, 0.6, 1, 0.4, 0.7].map((h, i) => (
                    <span
                        key={i}
                        className="w-[3px] rounded-full bg-gradient-to-t from-[#3B6FFF] to-[#94B4FF]"
                        style={{
                            height: `${h * 100}%`,
                            animation: "dg-wave 1.1s ease-in-out infinite",
                            animationDelay: `${i * 0.08}s`,
                        }}
                    />
                ))}
            </div>
            <span className="text-xs text-[#7A7A90]">
                {playing
                    ? "Tocando ligação real…"
                    : "Ouça uma ligação real agendada"}
            </span>
            <audio
                ref={audioRef}
                src={CALL_AUDIO_SRC}
                preload="none"
                onEnded={() => setPlaying(false)}
            />
        </div>
    </div>
    );
};

const phrases = ["Seu cliente ligou às 20h.", "Seu cliente chamou às 22h."];

export const Hero = () => {
    const [phraseIndex, setPhraseIndex] = useState(0);
    useEffect(() => {
        const t = setInterval(
            () => setPhraseIndex((i) => (i + 1) % phrases.length),
            3500
        );
        return () => clearInterval(t);
    }, []);
    return (
        <section
            id="top"
            className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
        >
            {/* background layers */}
            <div className="absolute inset-0 dg-grid opacity-40" />
            <div className="absolute -top-40 -right-40 h-[600px] w-[600px] dg-glow" />
            <div className="absolute top-1/3 -left-40 h-[500px] w-[500px] dg-glow opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0F]" />

            <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center w-full">
                {/* left */}
                <div>
                    <motion.div
                        data-testid="hero-brand-signature"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-8"
                    >
                        <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
                            DEGASPERIN <span className="text-[#3B6FFF]">IA</span>
                        </div>
                        <div className="text-sm text-[#7A7A90] mt-1">{AUTHOR}</div>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#F5F5F5]"
                    >
                        <span className="block min-h-[1.15em]">
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={phraseIndex}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -14 }}
                                    transition={{ duration: 0.5 }}
                                    className="inline-block"
                                >
                                    {phrases[phraseIndex]}
                                </motion.span>
                            </AnimatePresence>
                        </span>
                        <span className="text-[#7A7A90]">Ninguém atendeu.</span>{" "}
                        <span className="text-[#3B6FFF]">Ele foi embora.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-6 text-base lg:text-lg text-[#F5F5F5]/70 leading-relaxed max-w-xl"
                    >
                        Eu implemento um agente de IA que atende, agenda e lembra os
                        seus clientes 24h por dia — no WhatsApp ou na ligação. Sem
                        plataforma nova pra você aprender. Sem funcionário a mais.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                        className="mt-9 flex flex-col sm:flex-row gap-4"
                    >
                        <a
                            data-testid="hero-primary-cta"
                            href={WHATSAPP_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#3B6FFF] px-7 py-3.5 font-semibold text-white shadow-[0_0_30px_rgba(59,111,255,0.4)] hover:shadow-[0_0_45px_rgba(59,111,255,0.6)] hover:bg-[#2d5be0] transition-all"
                        >
                            <WhatsAppIcon className="w-5 h-5" />
                            Experimentar agora no WhatsApp
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                        <a
                            data-testid="hero-secondary-cta"
                            href={TEL_LINK}
                            className="group inline-flex items-center justify-center gap-2 rounded-full border border-[#1E1E28] bg-[#121218] px-7 py-3.5 font-semibold text-[#F5F5F5] hover:border-[#3B6FFF]/50 transition-colors"
                        >
                            <Phone className="w-4 h-4" />
                            Ligar e testar ao vivo
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                    </motion.div>
                </div>

                {/* right - agent demo */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                >
                    <ChatDemo />
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
